import { IconButton, makeStyles } from '@material-ui/core'
import LayersIcon from '@material-ui/icons/Layers'
import MapIcon from '@material-ui/icons/MapOutlined'
import SatelliteIcon from '@material-ui/icons/Satellite'
import StraightenIcon from '@mui/icons-material/Straighten'
import { ParentSize } from '@visx/responsive'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { withRouter } from 'react-router-dom'

import api from 'src/api'
import {
  AreaResponse,
  Favorite_PageIdEnum,
  OffenseMapAggregation,
  PolicyNameArea,
  Stops_LineTypeEnum,
} from 'src/api/gen'
import useComponentControl from 'src/commons/hooks/useComponentControl'
import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import useSafeState from 'src/commons/hooks/useSafeState'
import { useURLAndStorageState } from 'src/commons/hooks/useURLAndStorageState'
import { DrawLayer, GeoJSONLayer, MapActionBar, MapBoxMap, MapPopup, MapSpinner } from 'src/commons/map'
import LayersPopup from 'src/commons/map/LayersPopup'
import OffensePopup, { IClickedOffensePoint } from 'src/commons/map/OffensePopup'
import { addOffensesToMap } from 'src/commons/map/offenses'
import StopPopup from 'src/commons/map/StopPopup'
import { addStopsToMap, clearStopsMap, IClickedStopPoint } from 'src/commons/map/stops'
import { IMapSettings } from 'src/commons/map/types'
import { addMapImage, clearMap, getSatelliteFromStorage, MAP_VIEW_STORAGE } from 'src/commons/map/utils'
import { getTemporalRange, ZonedDateTimeToJSON } from 'src/commons/temporalHelpers'
import { POLICY_TYPES_ICONS, VIOLATION_MAP_POLICY_KINDS } from 'src/components/Offenses'
import { useProviders } from 'src/layout/hooks'
import { FilteringCard } from 'src/muiv5/components/commons/Cards/FilteringCard'

import MeasurementPanel from '../SupervisionMap/MeasurementPanel'

import ViolationMapFilterPanel, { IViolationMapFilters } from './MapFilterPanel'

export function getInitialFilters(): IMapSettings & IViolationMapFilters {
  return {
    provider: [],
    satellite: getSatelliteFromStorage() ?? '0',
    dateRange: getTemporalRange({ days: 7 }, true),
    include_abnormals: false,
    policyKind: [],
    policyId: [],
    eventType: [],
    deviceTypes: [],
    stopsLayers: [],
  }
}

function ViolationMap() {
  const classes = useStyles()

  const [map, setMap] = useSafeState<mapboxgl.Map | undefined>(undefined)
  const [areas, setAreas] = useSafeState<AreaResponse[]>([])
  const { activeProviders } = useProviders()
  const [mapInitialized, setMapInitialized] = useSafeState<boolean>(false)
  const [clickedOffensePoint, setClickedOffensePoint] = useSafeState<IClickedOffensePoint | undefined>(undefined)
  const [clickedStopPoint, setClickedStopPoint] = useSafeState<IClickedStopPoint | undefined>(undefined)
  const [areViolationsLoading, setAreViolationsLoading] = useSafeState(true)
  const [areStopsLoading, setAreStopsLoading] = useSafeState(true)
  const [policies, setPolicies] = useSafeState<PolicyNameArea[]>([])
  const [violationsCount, setViolationsCount] = useSafeState<number>(0)
  const [measure, setMeasure] = useSafeState(false)
  const [violationsFacets, setViolationsFacets] = useSafeState<OffenseMapAggregation>({
    policy_ids: {},
    policy_types: {},
    providers: {},
    vehicle_types: {},
    total: 0,
  })
  const defaultFilters = useMemo(getInitialFilters, [])
  const [filters, setFilters] = useURLAndStorageState(Favorite_PageIdEnum.policies_violation_map, defaultFilters)
  const displayedFiltersRef = useRef<IViolationMapFilters>()
  const [displayStopsPopup, setDisplayStopsPopup] = useState<boolean>(false)

  useEffect(() => {
    async function fetchPolicies() {
      const policies = await api.policies.policiesAreasAndNames({ kind: VIOLATION_MAP_POLICY_KINDS })
      setPolicies(policies)
    }
    try {
      fetchPolicies()
    } catch (error) {
      console.error(error)
    }
  }, [])

  useDeepCompareEffect(() => {
    setClickedOffensePoint(undefined)
    setClickedStopPoint(undefined)
    fetchViolations()
    fetchStops()
  }, [map])

  const fetchViolations = async function () {
    setAreViolationsLoading(true)

    const response = await api.offenses.offenseMapList({
      end_date_after: filters.dateRange.from ? ZonedDateTimeToJSON(filters.dateRange.from) : undefined,
      policy_ids: filters.policyId,
      policy_types: filters.policyKind,
      provider_ids: filters.provider,
      vehicle_type: filters.deviceTypes,
      start_date_before: filters.dateRange.to ? ZonedDateTimeToJSON(filters.dateRange.to) : undefined,
    })
    setAreViolationsLoading(false)

    setViolationsCount(response.aggregations.total)
    setViolationsFacets(response.aggregations)

    if (map) {
      clearMap(map)
      addOffensesToMap(map, response.results, activeProviders, setClickedOffensePoint, () => setMapInitialized(true))
      onMapReady(map)
    }
  }

  const fetchStops = async function () {
    setAreStopsLoading(true)
    const response = await api.stops.stopsList({ line_type: filters.stopsLayers })
    setAreStopsLoading(false)

    if (map) {
      clearStopsMap(map)
      if (filters.stopsLayers.length > 0) {
        addStopsToMap(map, response.results, setClickedStopPoint, () => setMapInitialized(true))
      }
      onMapReady(map)
    }
  }

  useDeepCompareEffect(() => {
    async function fetchPolicyAreas() {
      const active_policies = policies.filter((policy) => filters.policyId.includes(policy.id))
      const area_ids = active_policies.map((policy) => policy?.areas?.map((area) => area.pk?.toString())).flat()
      const fetchedAreas = (await api.areas.serviceAreasList()).filter((area) => area_ids.includes(area.id))
      setAreas(fetchedAreas || [])
    }

    try {
      fetchPolicyAreas()
    } catch (error) {
      console.error(error)
    }
    setClickedOffensePoint(undefined)
    setClickedStopPoint(undefined)
    fetchViolations()
    fetchStops()
  }, [filters])

  function setSatellite(value: boolean) {
    const newValue = value ? '1' : '0'
    setFilters((pendingFilters) => ({
      ...pendingFilters,
      satellite: newValue,
    }))
    localStorage.setItem(MAP_VIEW_STORAGE, newValue)
  }

  function toggleLayer(label: Stops_LineTypeEnum) {
    const newValue = filters.stopsLayers.includes(label)
      ? filters.stopsLayers.filter((layer) => layer !== label)
      : [...filters.stopsLayers, label]
    setFilters({ ...filters, stopsLayers: newValue })
  }

  async function onMapReady(currentMap: mapboxgl.Map) {
    await Promise.all(
      Object.entries(POLICY_TYPES_ICONS).map(([category, icon]) => addMapImage(currentMap, icon as string, category))
    )
  }

  async function onApplyFilterClick() {
    if (!displayedFiltersRef.current) {
      throw new Error('There are no displayed filters')
    }

    setFilters((pendingFilters) => ({ ...pendingFilters, ...displayedFiltersRef.current }))
  }

  const componentControl = useComponentControl(
    <>
      <MapActionBar>
        <IconButton onClick={() => setDisplayStopsPopup(!displayStopsPopup)}>
          <LayersIcon />
        </IconButton>
        <IconButton onClick={() => setSatellite(!(filters.satellite.toString() === '1'))}>
          {filters.satellite.toString() === '1' ? <MapIcon /> : <SatelliteIcon />}
        </IconButton>
      </MapActionBar>
      <MapActionBar fitContent>
        <IconButton onClick={() => setMeasure(!measure)}>
          <StraightenIcon color={measure ? 'primary' : 'inherit'} />
        </IconButton>
      </MapActionBar>
    </>,
    [filters.satellite, displayStopsPopup, measure]
  )

  return (
    <FilteringCard
      SideComponent={
        <ViolationMapFilterPanel
          ref={displayedFiltersRef}
          initialFilters={filters}
          defaultFilters={defaultFilters}
          providers={activeProviders}
          policies={policies}
          violationsCount={violationsCount}
          violationsFacets={violationsFacets}
          displayProviderColor={true}
        />
      }
      onApplyFilter={onApplyFilterClick}
    >
      <ParentSize parentSizeStyles={{ height: '100%', width: '100%' }}>
        {({ width, height }) =>
          !width || !height ? null : (
            <MapBoxMap
              width={width}
              height={height}
              onMapReady={(m) => setMap(m)}
              showSatellite={filters.satellite.toString() === '1'}
              showScale="bottom-right"
              showSearchBar="top-left"
              componentControl={componentControl}
            >
              {mapInitialized &&
                areas.map((area) => (
                  <GeoJSONLayer
                    key={area.label}
                    id={area.label}
                    geoData={{
                      type: 'FeatureCollection',
                      features: area.polygons.map((polygon) => ({
                        type: 'Feature',
                        geometry: polygon.geom,
                        properties: { id: area.label },
                      })) as any,
                    }}
                    layerOptions={{
                      paint: { 'fill-color': area.color, 'fill-opacity': 0.15 },
                    }}
                  />
                ))}
              {clickedOffensePoint && (
                <MapPopup coordinates={clickedOffensePoint.coordinates} verticalOffset={-20}>
                  <OffensePopup offense={clickedOffensePoint.offense} />
                </MapPopup>
              )}
              {clickedStopPoint && clickedStopPoint.stop && (
                <MapPopup coordinates={clickedStopPoint.coordinates} verticalOffset={-20}>
                  <StopPopup stop={clickedStopPoint.stop} provider={clickedStopPoint.provider} />
                </MapPopup>
              )}
              {displayStopsPopup && <LayersPopup toggleLayer={toggleLayer} activeStopsLayers={filters.stopsLayers} />}
              <MapSpinner
                classes={{ root: classes.mapSpinnerRoot }}
                loading={!mapInitialized || areViolationsLoading || areStopsLoading}
              />
              <DrawLayer active={measure} mode="draw_line_string">
                <MeasurementPanel onCancel={() => setMeasure(false)} />
              </DrawLayer>
            </MapBoxMap>
          )
        }
      </ParentSize>
    </FilteringCard>
  )
}

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: 'flex',
  },
  content: {
    width: '100%',
  },
  mapSpinnerRoot: {
    paddingLeft: '240px', // Panel width
  },
})

export default withRouter(ViolationMap)
